import React, { useEffect } from "react";
import Label from "./Label";
import { TextField } from "./fields";
import { formatDateOnly } from "@/utils/formatters/dateUtils";

interface DateFieldProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean | string;
  hint?: string;
  className?: string;
  minDate?: string;
  maxDate?: string;
}

const DateField: React.FC<DateFieldProps> = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  label,
  placeholder = "mm/dd/yyyy",
  required = false,
  error = false,
  hint,
  className = "",
  minDate,
  maxDate,
}) => {
  // Convert any date string to ISO format for the input
  const formatDateForInput = (dateStr: string): string => {
    if (!dateStr) return "";
    
    // If already in yyyy-MM-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Try to parse the date
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return formatDateOnly(date); // yyyy-MM-dd format
      }
    } catch (e) {
      console.error("Error parsing date:", dateStr, e);
    }
    
    return dateStr; // Return original if parsing fails
  };
  
  // Format the value for display in the input
  const formattedValue = formatDateForInput(value);
  
  // Better date handling - support both HTML5 date format (yyyy-mm-dd) and user-friendly format
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    console.log(`DateField onChange: ${name} = ${inputValue}`);
    
    // Save the value immediately in the form state
    onChange(e);
    
    // For debugging, log any potential date format issues
    if (inputValue && !/^\d{4}-\d{2}-\d{2}$/.test(inputValue)) {
      console.warn(`DateField format warning: ${name} value "${inputValue}" is not in yyyy-mm-dd format`);
      
      // Try to convert and update the format
      const formattedDate = formatDateForInput(inputValue);
      if (formattedDate && formattedDate !== inputValue) {
        console.log(`Converting date format on change: ${inputValue} → ${formattedDate}`);
        
        // Create a synthetic event with the formatted date
        const syntheticEvent = {
          target: { name, value: formattedDate }
        } as React.ChangeEvent<HTMLInputElement>;
        
        onChange(syntheticEvent);
      }
    }
  };
  
  // Custom blur handler to ensure date is properly formatted
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // If the field is empty, don't do anything special
    if (!e.target.value) {
      if (onBlur) onBlur(e);
      return;
    }
    
    // Try to format the date on blur
    const formattedDate = formatDateForInput(e.target.value);
    if (formattedDate !== e.target.value) {
      console.log(`Converting date format on blur: ${e.target.value} → ${formattedDate}`);
      
      // Create a synthetic event with the formatted date
      const syntheticEvent = {
        target: { name, value: formattedDate }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(syntheticEvent);
    }
    
    // Call the original onBlur handler if provided
    if (onBlur) onBlur(e);
  };
  
  // On component mount/update, ensure date format is consistent
  useEffect(() => {
    // If we have a value but it's not in the proper format, fix it
    if (value && value !== formattedValue) {
      console.log(`DateField format issue detected: ${name} value "${value}" → "${formattedValue}"`);
      
      // Create a synthetic event to update the value
      const syntheticEvent = {
        target: { name, value: formattedValue }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(syntheticEvent);
    }
  }, [name, value, formattedValue, onChange]);
  
  return (
    <div className={className}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <TextField
        id={id}
        name={name}
        type="date"
        placeholder={placeholder}
        value={formattedValue} // Use the formatted value
        onChange={handleDateChange}
        onBlur={handleBlur} // Use custom blur handler
        error={!!error}
        hint={typeof error === 'string' ? error : hint}
        required={required}
        min={minDate}
        max={maxDate}
      />
    </div>
  );
};

export default DateField; 